import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import {
  BOARD_CARD_KEY_PATTERN,
  suggestBoardKey,
  type CardAgingMode,
  type BoardVisibility,
  type BoardVisibilityPatch,
  type CommentingPermission,
  type MemberAddRemovePermission,
  type VotingPermission,
} from '@molt/shared';
import { Button, Menu, Notice, TextField, type MenuItem } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';
import { BoardVisibilityControl } from './BoardVisibilityControl';

export interface BoardSettingsSectionProps {
  visibility: BoardVisibility;
  canChangeVisibility: boolean;
  visibilityDisabledReason: string | null;
  onChangeVisibility: (patch: BoardVisibilityPatch) => void;
  visibilityBusy?: boolean | undefined;
  visibilityError?: string | undefined;
  /**
   * `FE-67.1` / `FR-PERM-011` AC1 — *"Given a board in a workspace not linked
   * to an Enterprise, when its admin opens the visibility picker, then this
   * level is not offered."*
   *
   * Forwarded verbatim to `BoardVisibilityControl`, which owns the suppression
   * and the one exception to it. This section decides nothing: the fact is
   * derived from `GET /workspaces/:id/enterprise` by `BoardMenuPanel`, the only
   * component on the path that holds a `workspaceId` and a query client.
   *
   * ⚠ **Optional, and `undefined` reads as PERMISSIVE** — the control defaults
   * it to `true`. That polarity is the whole safety property: a caller that
   * forgets the prop renders today's four-level picker, not a silently narrowed
   * one. Written the other way round it would suppress the Enterprise row
   * across the product with nothing red anywhere.
   *
   * Declared `| undefined` and passed plainly at both hops. A conditional JSX
   * spread is a hole in `tsc`, not a style choice.
   */
  offerEnterpriseVisibility?: boolean | undefined;
  /** One admin/settings capability: all B38 setting PATCHes use board-admin role. */
  canChangePermissions: boolean;
  permissionDisabledReason: string | null;
  /** `undefined` while the query is in flight — render "Checking…", not a guessed value. */
  memberPermission?: MemberAddRemovePermission | undefined;
  commentingPermission?: CommentingPermission | undefined;
  selfJoinEnabled?: boolean | undefined;
  votingEnabled?: boolean | undefined;
  votingPermission?: VotingPermission | undefined;
  votingHideTallies?: boolean | undefined;
  cardAgingMode?: CardAgingMode | undefined;
  onChangeMemberPermission: (value: MemberAddRemovePermission) => void;
  onChangeCommentingPermission: (value: CommentingPermission) => void;
  onChangeSelfJoin: (enabled: boolean) => void;
  onChangeVotingEnabled: (enabled: boolean) => void;
  onChangeVotingPermission: (value: VotingPermission) => void;
  onChangeVotingHideTallies: (hidden: boolean) => void;
  onChangeCardAgingMode: (value: CardAgingMode) => void;
  memberPermissionBusy?: boolean | undefined;
  commentingPermissionBusy?: boolean | undefined;
  selfJoinBusy?: boolean | undefined;
  votingEnabledBusy?: boolean | undefined;
  votingPermissionBusy?: boolean | undefined;
  votingHideTalliesBusy?: boolean | undefined;
  cardAgingModeBusy?: boolean | undefined;
  memberPermissionError?: string | undefined;
  commentingPermissionError?: string | undefined;
  selfJoinError?: string | undefined;
  votingEnabledError?: string | undefined;
  votingPermissionError?: string | undefined;
  votingHideTalliesError?: string | undefined;
  cardAgingModeError?: string | undefined;
  /**
   * `FR-CARD-005`'s optional board setting (`docs/notes/card-keys.md`) —
   * `boardContentsBoardSchema.showCardNumbersOnFront`. Required, unlike the
   * facet values above: it arrives on the board the panel already holds, so
   * there is no in-flight state to render.
   */
  showCardNumbersOnFront: boolean;
  onChangeShowCardNumbers: (shown: boolean) => void;
  showCardNumbersBusy?: boolean | undefined;
  showCardNumbersError?: string | undefined;
  /**
   * PR-list approvals threshold (`boardContentsBoardSchema.requiredApprovals`,
   * 1 to 20). Required for the same reason `showCardNumbersOnFront` is: it
   * arrives on the board the panel already holds.
   */
  requiredApprovals: number;
  onSaveRequiredApprovals: (value: number) => void;
  requiredApprovalsBusy?: boolean | undefined;
  requiredApprovalsError?: string | undefined;
  /**
   * Dangerous-comments threshold (`boardContentsBoardSchema.dangerousComments`,
   * 1 to 10000, `null` = off). Optional so older callers render unchanged.
   */
  dangerousComments?: number | null | undefined;
  onSaveDangerousComments?: ((value: number | null) => void) | undefined;
  dangerousCommentsBusy?: boolean | undefined;
  dangerousCommentsError?: string | undefined;
  /**
   * The board key (`MG` in `MG-0001`) and the title its placeholder is
   * previewed from with `suggestBoardKey` — a PREVIEW only: the database
   * derives stored keys, and the editor's value is the board's current key.
   */
  cardKey: string;
  boardTitle: string;
  onSaveCardKey: (key: string) => void;
  cardKeyBusy?: boolean | undefined;
  /**
   * `409 key_taken` — the one refusal the editor cannot prevent (another
   * board holds the key), so it gets its own sentence here. Any other failure
   * (a closed board's 409, a 403) shows `cardKeyError`, the server's message,
   * as every other setting in this section does.
   */
  cardKeyTaken?: boolean | undefined;
  cardKeyError?: string | undefined;
  className?: string;
}

/**
 * The board-key editor — `PATCH /boards/:id/key`, admin only (the same
 * capability every setting here takes). The value starts as the current key;
 * Save is offered only for a changed key that passes the route's grammar, so
 * the one refusal left to show inline is the 409 `key_taken` the panel maps.
 * The input upper-cases as the reader types, because the route does and a
 * lower-case value that saved as upper case would look like a silent edit.
 */
function CardKeyEditor({
  cardKey,
  boardTitle,
  canChange,
  disabledReason,
  busy,
  error,
  onSave,
}: {
  cardKey: string;
  boardTitle: string;
  canChange: boolean;
  disabledReason: string | null;
  busy: boolean | undefined;
  error: string | undefined;
  onSave: (key: string) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const [draft, setDraft] = useState(cardKey);
  // The key last sent. A refusal belongs to THAT key: once the reader edits
  // the draft, the old "already used" sentence would describe a key that is
  // no longer in the field, so the server's error shows only while they match.
  const [submitted, setSubmitted] = useState<string | null>(null);
  // A key changed elsewhere (another tab, over the socket) replaces the draft.
  useEffect(() => setDraft(cardKey), [cardKey]);

  const trimmed = draft.trim();
  const valid = BOARD_CARD_KEY_PATTERN.test(trimmed);
  const changed = trimmed !== cardKey;
  const reason = settingDisabledReason(t, canChange, true, disabledReason);
  const serverError = submitted === trimmed ? error : undefined;
  const fieldError =
    serverError ??
    (changed && trimmed !== '' && !valid
      ? t('boardSettings.cardKey.invalid')
      : undefined);
  const busyProps = busy === undefined ? {} : { busy };
  const reasonProps = reason === null ? {} : { disabledReason: reason };

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason === null && valid && changed) {
          setSubmitted(trimmed);
          onSave(trimmed);
        }
      }}
    >
      <TextField
        label={t('boardSettings.cardKey.label')}
        value={draft}
        placeholder={suggestBoardKey(boardTitle)}
        maxLength={10}
        autoCapitalize="characters"
        spellCheck={false}
        readOnly={reason !== null}
        onChange={(event) => setDraft(event.target.value.toUpperCase())}
        {...(fieldError === undefined ? {} : { error: fieldError })}
        wrapperClassName="w-40"
      />
      <Button
        type="submit"
        variant="secondary"
        disabled={reason !== null || !valid || !changed}
        {...reasonProps}
        {...busyProps}
      >
        {t('boardSettings.cardKey.save')}
      </Button>
    </form>
  );
}

function RequiredApprovalsEditor({
  requiredApprovals,
  canChange,
  disabledReason,
  busy,
  error,
  onSave,
}: {
  requiredApprovals: number;
  canChange: boolean;
  disabledReason: string | null;
  busy: boolean | undefined;
  error: string | undefined;
  onSave: (value: number) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const [draft, setDraft] = useState(String(requiredApprovals));
  const [submitted, setSubmitted] = useState<number | null>(null);
  // A threshold changed elsewhere (another tab, over the socket) replaces the draft.
  useEffect(() => setDraft(String(requiredApprovals)), [requiredApprovals]);

  const parsed = /^\d{1,2}$/.test(draft.trim()) ? Number(draft.trim()) : Number.NaN;
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 20;
  const changed = valid && parsed !== requiredApprovals;
  const reason = settingDisabledReason(t, canChange, true, disabledReason);
  const serverError = submitted === parsed ? error : undefined;
  const fieldError =
    serverError ??
    (draft.trim() !== String(requiredApprovals) && !valid
      ? t('boardSettings.requiredApprovals.invalid')
      : undefined);
  const busyProps = busy === undefined ? {} : { busy };
  const reasonProps = reason === null ? {} : { disabledReason: reason };

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason === null && changed) {
          setSubmitted(parsed);
          onSave(parsed);
        }
      }}
    >
      <TextField
        label={t('boardSettings.requiredApprovals.label')}
        type="number"
        inputMode="numeric"
        min={1}
        max={20}
        step={1}
        value={draft}
        readOnly={reason !== null}
        onChange={(event) => setDraft(event.target.value)}
        {...(fieldError === undefined ? {} : { error: fieldError })}
        wrapperClassName="w-32"
      />
      <Button
        type="submit"
        variant="secondary"
        disabled={reason !== null || !changed}
        {...reasonProps}
        {...busyProps}
      >
        {t('boardSettings.requiredApprovals.save')}
      </Button>
    </form>
  );
}

function DangerousCommentsEditor({
  dangerousComments,
  canChange,
  disabledReason,
  busy,
  error,
  onSave,
}: {
  dangerousComments: number | null;
  canChange: boolean;
  disabledReason: string | null;
  busy: boolean | undefined;
  error: string | undefined;
  onSave: (value: number | null) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const current = dangerousComments === null ? '' : String(dangerousComments);
  const [draft, setDraft] = useState(current);
  const [submitted, setSubmitted] = useState<string | null>(null);
  useEffect(() => setDraft(current), [current]);

  const trimmed = draft.trim();
  // An empty field is a valid value: it turns the highlight off (`null`).
  const parsed: number | null =
    trimmed === '' ? null : /^\d{1,5}$/.test(trimmed) ? Number(trimmed) : Number.NaN;
  const valid =
    parsed === null || (Number.isInteger(parsed) && parsed >= 1 && parsed <= 10000);
  const changed = valid && trimmed !== current;
  const reason = settingDisabledReason(t, canChange, true, disabledReason);
  const serverError = submitted === trimmed ? error : undefined;
  const fieldError =
    serverError ??
    (trimmed !== current && !valid
      ? t('boardSettings.dangerousComments.invalid')
      : undefined);
  const busyProps = busy === undefined ? {} : { busy };
  const reasonProps = reason === null ? {} : { disabledReason: reason };

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason === null && changed) {
          setSubmitted(trimmed);
          onSave(parsed);
        }
      }}
    >
      <TextField
        label={t('boardSettings.dangerousComments.label')}
        type="number"
        inputMode="numeric"
        min={1}
        max={10000}
        step={1}
        value={draft}
        readOnly={reason !== null}
        onChange={(event) => setDraft(event.target.value)}
        {...(fieldError === undefined ? {} : { error: fieldError })}
        wrapperClassName="w-48"
      />
      <Button
        type="submit"
        variant="secondary"
        disabled={reason !== null || !changed}
        {...reasonProps}
        {...busyProps}
      >
        {t('boardSettings.dangerousComments.save')}
      </Button>
    </form>
  );
}

/*
 * ---------------------------------------------------------------------------
 * FE-80.1 — THE EIGHT RECORDS ARE FUNCTIONS OF `t`, AND EVERY ENTRY IS A
 * LITERAL `t('…')` CALL
 * ---------------------------------------------------------------------------
 * A module-level constant cannot call `t` (`lib/card-title.ts`'s docstring is
 * the settled statement of why), so each of the eight `Record<Enum, string>`
 * constants became a function returning the same record.
 *
 * ⚠ Written out entry by entry rather than as a record of KEYS read back
 * through `t(KEYS[value])`. The key-record form is tidier and hides all 34
 * values from the `/\bt\(\s*'([^']+)'/g` anchor every pin in this workstream
 * uses — nine times worse than the board-table slice's 4-of-29, which is the
 * residual that let a deleted call site pass a count guard. Every value here
 * stays anchor-visible.
 *
 * ⚠ `t` is the REQUIRED first parameter, never defaulted, here and on
 * `settingDisabledReason`. A defaulted translator lets one caller fall through
 * to a default that looks correct; only a required one makes `tsc` enumerate
 * the call sites.
 *
 * ⚠ THE RECORD KEYS ARE API VALUES AND STAY LITERAL. `admins`, `all_members`,
 * `disabled`, `members`, `members_and_observers`, `workspace_members`,
 * `any_user`, `off`, `regular`, `pirate` are enum members that the radio
 * section's `onValueChange` casts straight back into the PATCH body
 * (`value as CommentingPermission`). Only the record VALUES are displayed, and
 * only the values are routed.
 */
function memberPermissionLabels(
  t: LocaleFormattingTranslate,
): Record<MemberAddRemovePermission, string> {
  return {
    admins: t('boardSettings.memberPermission.option.admins'),
    all_members: t('boardSettings.memberPermission.option.allMembers'),
  };
}

function memberPermissionDescriptions(
  t: LocaleFormattingTranslate,
): Record<MemberAddRemovePermission, string> {
  return {
    admins: t('boardSettings.memberPermission.description.admins'),
    all_members: t('boardSettings.memberPermission.description.allMembers'),
  };
}

/**
 * ⚠ ORDER IS LOAD-BEARING AND WAS IMPLICIT. The menu was built from
 * `Object.entries(MEMBER_PERMISSION_LABELS)`, so the record's key order *was*
 * the menu order; naming the array makes that explicit and removes the
 * `value as MemberAddRemovePermission` cast the `Object.entries` form needed.
 * `admins` first, `all_members` second — `FR-PERM-025` and `FR-CARD-033`
 * assert against this order.
 */
const MEMBER_PERMISSION_OPTIONS: readonly MemberAddRemovePermission[] = [
  'admins',
  'all_members',
];

function commentingPermissionLabels(
  t: LocaleFormattingTranslate,
): Record<CommentingPermission, string> {
  return {
    disabled: t('boardSettings.commenting.option.disabled'),
    members: t('boardSettings.commenting.option.members'),
    members_and_observers: t('boardSettings.commenting.option.membersAndObservers'),
    workspace_members: t('boardSettings.commenting.option.workspaceMembers'),
    any_user: t('boardSettings.commenting.option.anyUser'),
  };
}

function commentingPermissionDescriptions(
  t: LocaleFormattingTranslate,
): Record<CommentingPermission, string> {
  return {
    disabled: t('boardSettings.commenting.description.disabled'),
    members: t('boardSettings.commenting.description.members'),
    members_and_observers: t('boardSettings.commenting.description.membersAndObservers'),
    workspace_members: t('boardSettings.commenting.description.workspaceMembers'),
    any_user: t('boardSettings.commenting.description.anyUser'),
  };
}

const COMMENTING_OPTIONS: readonly CommentingPermission[] = [
  'disabled',
  'members',
  'members_and_observers',
  'workspace_members',
  'any_user',
];

/**
 * ⚠ FOUR OF THESE VALUES ARE BYTE-IDENTICAL TO THEIR `commenting` twins and
 * are DECLARED SEPARATELY, not shared. Two independent API enums, two
 * different descriptions beside them, and `FR-CARD-033:75` / `FR-PERM-026:69`
 * each bind `/^Members and observers/` inside a *different* menu. The
 * catalogue comment above `boardSettings.commenting.option.disabled` carries
 * the full ruling.
 */
function votingPermissionLabels(
  t: LocaleFormattingTranslate,
): Record<VotingPermission, string> {
  return {
    disabled: t('boardSettings.votingPermission.option.disabled'),
    members: t('boardSettings.votingPermission.option.members'),
    members_and_observers: t('boardSettings.votingPermission.option.membersAndObservers'),
    workspace_members: t('boardSettings.votingPermission.option.workspaceMembers'),
  };
}

function votingPermissionDescriptions(
  t: LocaleFormattingTranslate,
): Record<VotingPermission, string> {
  return {
    disabled: t('boardSettings.votingPermission.description.disabled'),
    members: t('boardSettings.votingPermission.description.members'),
    members_and_observers: t(
      'boardSettings.votingPermission.description.membersAndObservers',
    ),
    workspace_members: t('boardSettings.votingPermission.description.workspaceMembers'),
  };
}

const VOTING_OPTIONS: readonly VotingPermission[] = [
  'disabled',
  'members',
  'members_and_observers',
  'workspace_members',
];

function cardAgingModeLabels(
  t: LocaleFormattingTranslate,
): Record<CardAgingMode, string> {
  return {
    off: t('boardSettings.cardAging.option.off'),
    regular: t('boardSettings.cardAging.option.regular'),
    pirate: t('boardSettings.cardAging.option.pirate'),
  };
}

function cardAgingModeDescriptions(
  t: LocaleFormattingTranslate,
): Record<CardAgingMode, string> {
  return {
    off: t('boardSettings.cardAging.description.off'),
    regular: t('boardSettings.cardAging.description.regular'),
    pirate: t('boardSettings.cardAging.description.pirate'),
  };
}

const CARD_AGING_OPTIONS: readonly CardAgingMode[] = ['off', 'regular', 'pirate'];

interface SettingsRowProps {
  heading: string;
  children: ReactNode;
}

function SettingsRow({ heading, children }: SettingsRowProps): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-sm font-semibold text-ink">{heading}</h3>
      {children}
    </div>
  );
}

/**
 * ⚠ THE LABEL SPAN STAYS FIRST. `FR-PERM-027:535` anchors `/^Board admins
 * only/` at the START of the menuitemradio's accessible name, which is the
 * concatenation of these two spans in DOM order. Reordering them for looks
 * breaks that spec for a reason that reads as a permissions bug.
 */
function optionLabel(title: string, description: string): ReactElement {
  return (
    <span className="flex flex-col items-start gap-0.5 py-0.5 text-left">
      <span className="text-card text-ink">{title}</span>
      <span className="text-xs text-ink-muted">{description}</span>
    </span>
  );
}

/**
 * ⚠ REFUSAL PRECEDENCE IS A PREMISE, NOT A DETAIL: `!valueKnown` wins over
 * `!canChange`. A permission-reduced viewer whose settings query has not
 * landed sees `Checking this board setting…`, never the locked reason — any
 * assertion about the locked sentence has to wait for the queries first.
 *
 * ⚠ THE `??` FALLBACK REUSES `boardMenu.reason.adminPermissionSettings`
 * RATHER THAN DECLARING THE SENTENCE AGAIN. `BoardMenuPanel.tsx:732` already
 * resolves that exact key into the `permissionDisabledReason` prop this
 * expression falls back from — one node, one name. A second declaration is the
 * `boardTable.viewLabel` defect: composed, the prop speaks the translation;
 * uncomposed, the fallback speaks English, and nothing goes red.
 */
function settingDisabledReason(
  t: LocaleFormattingTranslate,
  canChange: boolean,
  valueKnown: boolean,
  explicitReason: string | null,
): string | null {
  if (!valueKnown) return t('boardSettings.reason.checkingSetting');
  if (canChange) return null;
  return explicitReason ?? t('boardMenu.reason.adminPermissionSettings');
}

function SettingMenu({
  label,
  accessibleName,
  sectionId,
  value,
  items,
  canChange,
  disabledReason,
  busy,
  onChange,
}: {
  label: string;
  /**
   * ⚠ A WHOLE SLOTTED SENTENCE, resolved by the caller. This was
   * `` `${triggerLabel} — change ${heading.toLowerCase()}` `` — a join, which
   * no catalogue can translate, plus a `.toLowerCase()` that hands a
   * language-specific decision to JavaScript (German capitalises nouns;
   * Turkish `I` lowercases to a dotless `ı`).
   */
  accessibleName: string;
  /**
   * ⚠ AN ASCII SLUG, AND SEPARATE FROM THE ACCESSIBLE NAME ON PURPOSE. This
   * prop used to be the display heading, and `Menu.tsx:194` builds
   * `` `${baseId}-${section.id}-heading` `` out of it — a DOM id and a React
   * key made of a translated sentence. `baseId` is a `useId()`, so nothing
   * binds the generated id; the split is what keeps routing from translating
   * one.
   */
  sectionId: string;
  value: string | undefined;
  items: MenuItem[];
  canChange: boolean;
  disabledReason: string | null;
  busy: boolean | undefined;
  onChange: (value: string) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const valueKnown = value !== undefined;
  const triggerLabel = valueKnown ? label : t('boardSettings.checking');
  const reason = settingDisabledReason(t, canChange, valueKnown, disabledReason);

  if (reason !== null) {
    return (
      <Button
        type="button"
        variant="secondary"
        aria-label={accessibleName}
        disabled
        disabledReason={reason}
      >
        {triggerLabel}
      </Button>
    );
  }

  const busyProps = busy === undefined ? {} : { busy };

  return (
    <Menu
      trigger={
        <Button
          type="button"
          variant="secondary"
          aria-label={accessibleName}
          {...busyProps}
        >
          {triggerLabel}
        </Button>
      }
      sections={[
        {
          id: sectionId,
          selection: 'radio',
          value: value ?? '',
          onValueChange: onChange,
          items,
        },
      ]}
    />
  );
}

function SettingSwitch({
  label,
  accessibleName,
  value,
  canChange,
  disabledReason,
  busy,
  onChange,
}: {
  label: string;
  accessibleName: string;
  value: boolean | undefined;
  canChange: boolean;
  disabledReason: string | null;
  busy: boolean | undefined;
  onChange: (value: boolean) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const reason = settingDisabledReason(t, canChange, value !== undefined, disabledReason);
  const busyProps = busy === undefined ? {} : { busy };
  const disabledReasonProps = reason === null ? {} : { disabledReason: reason };

  return (
    <Button
      type="button"
      variant="secondary"
      aria-label={accessibleName}
      role="switch"
      aria-checked={value === true}
      disabled={reason !== null}
      {...disabledReasonProps}
      {...busyProps}
      onClick={() => {
        if (value !== undefined) onChange(!value);
      }}
    >
      {label}
    </Button>
  );
}

export function BoardSettingsSection({
  visibility,
  canChangeVisibility,
  visibilityDisabledReason,
  onChangeVisibility,
  visibilityBusy,
  visibilityError,
  offerEnterpriseVisibility,
  canChangePermissions,
  permissionDisabledReason,
  memberPermission,
  commentingPermission,
  selfJoinEnabled,
  votingEnabled,
  votingPermission,
  votingHideTallies,
  cardAgingMode,
  onChangeMemberPermission,
  onChangeCommentingPermission,
  onChangeSelfJoin,
  onChangeVotingEnabled,
  onChangeVotingPermission,
  onChangeVotingHideTallies,
  onChangeCardAgingMode,
  memberPermissionBusy,
  commentingPermissionBusy,
  selfJoinBusy,
  votingEnabledBusy,
  votingPermissionBusy,
  votingHideTalliesBusy,
  cardAgingModeBusy,
  memberPermissionError,
  commentingPermissionError,
  selfJoinError,
  votingEnabledError,
  votingPermissionError,
  votingHideTalliesError,
  cardAgingModeError,
  showCardNumbersOnFront,
  onChangeShowCardNumbers,
  showCardNumbersBusy,
  showCardNumbersError,
  requiredApprovals,
  onSaveRequiredApprovals,
  requiredApprovalsBusy,
  requiredApprovalsError,
  dangerousComments = null,
  onSaveDangerousComments,
  dangerousCommentsBusy,
  dangerousCommentsError,
  cardKey,
  boardTitle,
  onSaveCardKey,
  cardKeyBusy,
  cardKeyTaken,
  cardKeyError,
  className,
}: BoardSettingsSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const offeredCommentingOptions = COMMENTING_OPTIONS.filter(
    (value) => value !== 'any_user' || visibility === 'public',
  );
  const memberPermissionItems = MEMBER_PERMISSION_OPTIONS.map((value) => ({
    id: value,
    label: optionLabel(
      memberPermissionLabels(t)[value],
      memberPermissionDescriptions(t)[value],
    ),
    onSelect: () => undefined,
  }));
  const commentingPermissionItems = offeredCommentingOptions.map((value) => ({
    id: value,
    label: optionLabel(
      commentingPermissionLabels(t)[value],
      commentingPermissionDescriptions(t)[value],
    ),
    onSelect: () => undefined,
  }));
  const votingPermissionItems = VOTING_OPTIONS.map((value) => ({
    id: value,
    label: optionLabel(
      votingPermissionLabels(t)[value],
      votingPermissionDescriptions(t)[value],
    ),
    onSelect: () => undefined,
  }));
  const cardAgingModeItems = CARD_AGING_OPTIONS.map((value) => ({
    id: value,
    label: optionLabel(
      cardAgingModeLabels(t)[value],
      cardAgingModeDescriptions(t)[value],
    ),
    onSelect: () => undefined,
  }));
  const selfJoinOffered = visibility !== 'private';
  const selfJoinValueKnown = selfJoinEnabled !== undefined;
  /*
   * ⚠ THE `else` ARM IS UNREACHABLE, AND IS ROUTED ANYWAY. These props are
   * spread onto the Button that renders only when `selfJoinOffered` is TRUE,
   * and this arm is the `selfJoinOffered === false` branch; the `<p>` below is
   * the sentence a reader actually sees. Both ask for the one key, so the dead
   * arm cannot drift into a second language and the count guard can still see
   * it — deleting a site is deleting the thing that holds it.
   */
  const selfJoinReason = selfJoinOffered
    ? settingDisabledReason(
        t,
        canChangePermissions,
        selfJoinValueKnown,
        permissionDisabledReason,
      )
    : t('boardSettings.selfJoin.privateBoard');
  /*
   * ⚠ EVERY TRIGGER LABEL IS HOISTED TO A CONST because it is needed TWICE —
   * once as the visible label and once as the `{label}` slot of the whole
   * accessible-name sentence. Inlining the ternary at both sites would double
   * every one of these `t('boardSettings.checking')` call sites and make the
   * pin's count guard describe a shape nobody wrote.
   *
   * ⚠ AND THE TERNARIES ARE NOT DECORATION. `memberPermissionLabels(t)[…]`
   * does not typecheck while the value is `T | undefined`; the `undefined` arm
   * is what narrows the index. Four of the eight `Checking…` sites are
   * unreachable in practice — `SettingMenu` recomputes `triggerLabel` and
   * discards `label` exactly when the caller's ternary would have supplied
   * `Checking…` — and they are routed rather than deleted for the same reason
   * as the self-join arm above.
   */
  const memberPermissionLabel =
    memberPermission === undefined
      ? t('boardSettings.checking')
      : memberPermissionLabels(t)[memberPermission];
  const commentingPermissionLabel =
    commentingPermission === undefined
      ? t('boardSettings.checking')
      : commentingPermissionLabels(t)[commentingPermission];
  const votingPermissionLabel =
    votingPermission === undefined
      ? t('boardSettings.checking')
      : votingPermissionLabels(t)[votingPermission];
  const cardAgingModeLabel =
    cardAgingMode === undefined
      ? t('boardSettings.checking')
      : cardAgingModeLabels(t)[cardAgingMode];
  const selfJoinLabel =
    selfJoinEnabled === undefined
      ? t('boardSettings.checking')
      : selfJoinEnabled
        ? t('boardSettings.on')
        : t('boardSettings.off');
  const selfJoinBusyProps = selfJoinBusy === undefined ? {} : { busy: selfJoinBusy };
  const selfJoinDisabledReasonProps =
    selfJoinReason === null ? {} : { disabledReason: selfJoinReason };
  const votingEnabledLabel =
    votingEnabled === undefined
      ? t('boardSettings.checking')
      : votingEnabled
        ? t('boardSettings.on')
        : t('boardSettings.off');
  const cardNumbersLabel = showCardNumbersOnFront
    ? t('boardSettings.on')
    : t('boardSettings.off');
  const votingTallyLabel =
    votingHideTallies === undefined
      ? t('boardSettings.checking')
      : votingHideTallies
        ? t('boardSettings.tallies.hidden')
        : t('boardSettings.tallies.visible');

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <SettingsRow heading={t('boardSettings.heading.visibility')}>
        <BoardVisibilityControl
          visibility={visibility}
          canChange={canChangeVisibility}
          disabledReason={visibilityDisabledReason}
          onChange={onChangeVisibility}
          busy={visibilityBusy}
          error={visibilityError}
          offerEnterpriseVisibility={offerEnterpriseVisibility}
        />
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.memberPermission')}>
        <SettingMenu
          label={memberPermissionLabel}
          accessibleName={t('boardSettings.trigger.memberPermission', {
            label: memberPermissionLabel,
          })}
          sectionId="member-permission"
          value={memberPermission}
          items={memberPermissionItems}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={memberPermissionBusy}
          onChange={(value) => {
            onChangeMemberPermission(value as MemberAddRemovePermission);
          }}
        />
        {memberPermissionError !== undefined ? (
          <Notice tone="bad" body={memberPermissionError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.commenting')}>
        <SettingMenu
          label={commentingPermissionLabel}
          accessibleName={t('boardSettings.trigger.commenting', {
            label: commentingPermissionLabel,
          })}
          sectionId="commenting-permission"
          value={commentingPermission}
          items={commentingPermissionItems}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={commentingPermissionBusy}
          onChange={(value) => {
            onChangeCommentingPermission(value as CommentingPermission);
          }}
        />
        {commentingPermissionError !== undefined ? (
          <Notice tone="bad" body={commentingPermissionError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.voting')}>
        <SettingSwitch
          label={votingEnabledLabel}
          accessibleName={t('boardSettings.trigger.voting', {
            label: votingEnabledLabel,
          })}
          value={votingEnabled}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={votingEnabledBusy}
          onChange={onChangeVotingEnabled}
        />
        <p className="text-xs text-ink-muted">{t('boardSettings.voting.note')}</p>
        {votingEnabledError !== undefined ? (
          <Notice tone="bad" body={votingEnabledError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.votingPermission')}>
        <SettingMenu
          label={votingPermissionLabel}
          accessibleName={t('boardSettings.trigger.votingPermission', {
            label: votingPermissionLabel,
          })}
          sectionId="voting-permission"
          value={votingPermission}
          items={votingPermissionItems}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={votingPermissionBusy}
          onChange={(value) => {
            onChangeVotingPermission(value as VotingPermission);
          }}
        />
        {votingPermissionError !== undefined ? (
          <Notice tone="bad" body={votingPermissionError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.votingTallies')}>
        <SettingSwitch
          label={votingTallyLabel}
          accessibleName={t('boardSettings.trigger.votingTallies', {
            label: votingTallyLabel,
          })}
          value={votingHideTallies === undefined ? undefined : !votingHideTallies}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={votingHideTalliesBusy}
          onChange={(visible) => {
            onChangeVotingHideTallies(!visible);
          }}
        />
        <p className="text-xs text-ink-muted">{t('boardSettings.votingTallies.note')}</p>
        {votingHideTalliesError !== undefined ? (
          <Notice tone="bad" body={votingHideTalliesError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.cardAging')}>
        <SettingMenu
          label={cardAgingModeLabel}
          accessibleName={t('boardSettings.trigger.cardAging', {
            label: cardAgingModeLabel,
          })}
          sectionId="card-aging"
          value={cardAgingMode}
          items={cardAgingModeItems}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={cardAgingModeBusy}
          onChange={(value) => {
            onChangeCardAgingMode(value as CardAgingMode);
          }}
        />
        <p className="text-xs text-ink-muted">{t('boardSettings.cardAging.note')}</p>
        {cardAgingModeError !== undefined ? (
          <Notice tone="bad" body={cardAgingModeError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.cardNumbers')}>
        <SettingSwitch
          label={cardNumbersLabel}
          accessibleName={t('boardSettings.trigger.cardNumbers', {
            label: cardNumbersLabel,
          })}
          value={showCardNumbersOnFront}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={showCardNumbersBusy}
          onChange={onChangeShowCardNumbers}
        />
        <p className="text-xs text-ink-muted">{t('boardSettings.cardNumbers.note')}</p>
        {showCardNumbersError !== undefined ? (
          <Notice tone="bad" body={showCardNumbersError} />
        ) : null}
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.requiredApprovals')}>
        <RequiredApprovalsEditor
          requiredApprovals={requiredApprovals}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={requiredApprovalsBusy}
          error={requiredApprovalsError}
          onSave={onSaveRequiredApprovals}
        />
        <p className="text-xs text-ink-muted">
          {t('boardSettings.requiredApprovals.note')}
        </p>
      </SettingsRow>

      {onSaveDangerousComments !== undefined ? (
        <SettingsRow heading={t('boardSettings.heading.dangerousComments')}>
          <DangerousCommentsEditor
            dangerousComments={dangerousComments}
            canChange={canChangePermissions}
            disabledReason={permissionDisabledReason}
            busy={dangerousCommentsBusy}
            error={dangerousCommentsError}
            onSave={onSaveDangerousComments}
          />
          <p className="text-xs text-ink-muted">
            {t('boardSettings.dangerousComments.note')}
          </p>
        </SettingsRow>
      ) : null}

      <SettingsRow heading={t('boardSettings.heading.cardKey')}>
        <CardKeyEditor
          cardKey={cardKey}
          boardTitle={boardTitle}
          canChange={canChangePermissions}
          disabledReason={permissionDisabledReason}
          busy={cardKeyBusy}
          error={cardKeyTaken === true ? t('boardSettings.cardKey.taken') : cardKeyError}
          onSave={onSaveCardKey}
        />
        <p className="text-xs text-ink-muted">{t('boardSettings.cardKey.note')}</p>
      </SettingsRow>

      <SettingsRow heading={t('boardSettings.heading.selfJoin')}>
        {selfJoinOffered ? (
          <Button
            type="button"
            variant="secondary"
            aria-label={t('boardSettings.trigger.selfJoin', { label: selfJoinLabel })}
            role="switch"
            aria-checked={selfJoinEnabled === true}
            disabled={selfJoinReason !== null}
            {...selfJoinDisabledReasonProps}
            {...selfJoinBusyProps}
            onClick={() => {
              if (selfJoinEnabled !== undefined) onChangeSelfJoin(!selfJoinEnabled);
            }}
          >
            {selfJoinLabel}
          </Button>
        ) : (
          <p className="text-sm text-ink-muted">
            {t('boardSettings.selfJoin.privateBoard')}
          </p>
        )}
        <p className="text-xs text-ink-muted">{t('boardSettings.selfJoin.note')}</p>
        {selfJoinError !== undefined ? <Notice tone="bad" body={selfJoinError} /> : null}
      </SettingsRow>
    </div>
  );
}
